/**
 * 地图初始化 composable
 */
import { shallowRef, onBeforeUnmount } from 'vue';
import L from 'leaflet';
import { convertGeoJsonToGcj02 } from '@/utils/coord-transform';
import {
  MAP_OPTIONS,
  BOUNDARY_STYLE,
  PROVINCE_BORDER_STYLE,
  CITY_BORDER_STYLE,
  CITY_ZOOM_THRESHOLD
} from '@/config/map.config';
import airportData from '@/views/airport/China_airport_all.geojson';
import airportPointData from '@/views/airport/China_airport_all_point.geojson';
import provinceData from '@/views/airport/provi.geojson';
import cityData from '@/views/airport/city.geojson';

export function useMapInit() {
  const map = shallowRef(null);
  const airportLayer = shallowRef(null);
  const airportPointLayer = shallowRef(null);
  const airportLayerVisible = shallowRef(true);
  const provinceLabels = shallowRef(null);
  const cityLayer = shallowRef(null);
  const cityLabels = shallowRef(null);

  // 预转换坐标数据
  const airportDataGcj02 = convertGeoJsonToGcj02(airportData);
  const airportPointDataGcj02 = convertGeoJsonToGcj02(airportPointData);

  /**
   * 创建动态图标大小的函数
   */
  const createAirportIcon = (zoom, iconUrl) => {
    const baseSize = 8;
    const iconSize = Math.max(8, Math.min(24, baseSize + (zoom - 4) * 2));
    return L.icon({
      iconUrl,
      iconSize: [iconSize, iconSize],
      iconAnchor: [iconSize / 2, iconSize / 2],
      tooltipAnchor: [0, -iconSize / 2]
    });
  };

  /**
   * 初始化地图
   */
  const initMap = () => {
    const mapContainer = document.getElementById("map");
    if (!mapContainer) return;

    // 销毁已有地图实例
    if (map.value && map.value.remove) {
      map.value.remove();
    }

    // 创建底图图层
    const tileLayer = L.tileLayer("https://webst01.is.autonavi.com/appmaptile?style=6&x={x}&y={y}&z={z}");

    // 初始化地图
    map.value = L.map("map", {
      ...MAP_OPTIONS,
      layers: [tileLayer]
    });

    // 创建自定义 pane，按堆叠顺序：省(底) → 市 → 机场 → 云图(顶)
    map.value.createPane('provincePane');
    map.value.createPane('cityPane');
    map.value.createPane('airportPane');
    map.value.createPane('cloudPane');
    map.value.getPane('provincePane').style.zIndex = 401;
    map.value.getPane('cityPane').style.zIndex = 402;
    map.value.getPane('airportPane').style.zIndex = 403;
    map.value.getPane('cloudPane').style.zIndex = 404;

    // ============ 第一层：省界 ============
    L.geoJSON(provinceData, {
      style: PROVINCE_BORDER_STYLE,
      pane: 'provincePane'
    }).addTo(map.value);

    // 省会名称标签
    provinceLabels.value = L.layerGroup({ pane: 'provincePane' }).addTo(map.value);
    provinceData.features.forEach((feature) => {
      const props = feature.properties;
      if (props && props.name && props.level === 'province') {
        const pos = props.centroid || props.center;
        if (!pos) return;
        L.marker([pos[1], pos[0]], {
          icon: L.divIcon({
            className: 'province-label',
            html: `<span>${props.name}</span>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0]
          }),
          interactive: false,
          pane: 'provincePane'
        }).addTo(provinceLabels.value);
      }
    });

    // ============ 第二层：市界 ============
    cityLayer.value = L.layerGroup({ pane: 'cityPane' }).addTo(map.value);
    cityLabels.value = L.layerGroup({ pane: 'cityPane' }).addTo(map.value);

    let cityLoaded = false;
    const loadCityData = () => {
      const zoom = map.value.getZoom();
      if (zoom >= CITY_ZOOM_THRESHOLD) {
        if (cityLoaded) return;
        cityLoaded = true;

        const cityFeatures = cityData.features.filter(
          (f) => f.properties && f.properties.level !== 'province'
        );

        L.geoJSON({ type: 'FeatureCollection', features: cityFeatures }, {
          style: CITY_BORDER_STYLE,
          pane: 'cityPane'
        }).addTo(cityLayer.value);

        cityFeatures.forEach((f) => {
          const cp = f.properties;
          if (!cp || !cp.name) return;
          const pos = cp.centroid || cp.center;
          if (!pos) return;
          L.marker([pos[1], pos[0]], {
            icon: L.divIcon({
              className: 'city-label',
              html: `<span>${cp.name}</span>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0]
            }),
            interactive: false,
            pane: 'cityPane'
          }).addTo(cityLabels.value);
        });
      } else {
        cityLayer.value.clearLayers();
        cityLabels.value.clearLayers();
        cityLoaded = false;
      }
    };

    map.value.on('zoomend', loadCityData);

    // ============ 第三层：机场 ============
    const airportIconUrl = require('@/assets/airport.png');

    // 机场面图层
    airportLayer.value = L.geoJSON(airportDataGcj02, {
      style: BOUNDARY_STYLE,
      pane: 'airportPane',
      onEachFeature: (feature, layer) => {
        if (feature.properties && feature.properties.name) {
          layer.bindTooltip(feature.properties.name, {
            permanent: true,
            direction: 'top',
            className: 'airport-label',
            opacity: 0
          });
        }
      }
    });

    // 机场点图标图层
    airportPointLayer.value = L.layerGroup({ pane: 'airportPane' });
    airportPointDataGcj02.features.forEach((feature) => {
      if (feature.geometry && feature.geometry.coordinates) {
        const [lng, lat] = feature.geometry.coordinates;
        const marker = L.marker([lat, lng], {
          icon: createAirportIcon(map.value.getZoom(), airportIconUrl),
          pane: 'airportPane'
        });
        if (feature.properties && feature.properties.name) {
          marker.bindTooltip(feature.properties.name, {
            permanent: true,
            direction: 'top',
            className: 'airport-label',
            opacity: 0
          });
        }
        airportPointLayer.value.addLayer(marker);
      }
    });
    airportPointLayer.value.addTo(map.value);

    // 机场图层三级缩放控制
    const updateAirportDisplay = () => {
      if (!airportLayer.value || !airportPointLayer.value) return;
      // 若用户已手动关闭机场图层，缩放时不自动恢复
      if (!airportLayerVisible.value) return;

      const zoom = map.value.getZoom();

      // 更新图标大小
      airportPointLayer.value.eachLayer((layer) => {
        if (layer.setIcon) {
          layer.setIcon(createAirportIcon(zoom, airportIconUrl));
        }
      });

      if (zoom < 9) {
        if (map.value.hasLayer(airportLayer.value)) map.value.removeLayer(airportLayer.value);
        if (!map.value.hasLayer(airportPointLayer.value)) airportPointLayer.value.addTo(map.value);
        airportPointLayer.value.eachLayer((layer) => {
          const tooltip = layer.getTooltip();
          if (tooltip) tooltip.setOpacity(0);
        });
      } else if (zoom === 9) {
        if (map.value.hasLayer(airportLayer.value)) map.value.removeLayer(airportLayer.value);
        if (!map.value.hasLayer(airportPointLayer.value)) airportPointLayer.value.addTo(map.value);
        airportPointLayer.value.eachLayer((layer) => {
          const tooltip = layer.getTooltip();
          if (tooltip) {
            layer.openTooltip();
            tooltip.setOpacity(1);
          }
        });
      } else {
        if (map.value.hasLayer(airportPointLayer.value)) map.value.removeLayer(airportPointLayer.value);
        if (!map.value.hasLayer(airportLayer.value)) airportLayer.value.addTo(map.value);
        airportLayer.value.eachLayer((layer) => {
          const tooltip = layer.getTooltip();
          if (tooltip) {
            layer.openTooltip();
            tooltip.setOpacity(1);
          }
        });
      }
    };

    map.value.on('zoomend', updateAirportDisplay);
    updateAirportDisplay();

    // 瓦片加载错误监听
    tileLayer.on("error", () => {});
  };

  /**
   * 切换机场图层显隐
   */
  const toggleAirportLayer = () => {
    if (!map.value) return;
    if (airportLayerVisible.value) {
      if (map.value.hasLayer(airportLayer.value)) map.value.removeLayer(airportLayer.value);
      if (map.value.hasLayer(airportPointLayer.value)) map.value.removeLayer(airportPointLayer.value);
      airportLayerVisible.value = false;
    } else {
      airportLayerVisible.value = true;
      // 恢复时根据当前缩放级别正确显示
      const zoom = map.value.getZoom();
      if (zoom <= 9) {
        airportPointLayer.value.addTo(map.value);
        // 根据zoom决定标签是否可见
        airportPointLayer.value.eachLayer((layer) => {
          const tooltip = layer.getTooltip();
          if (tooltip) tooltip.setOpacity(zoom === 9 ? 1 : 0);
        });
      } else {
        airportLayer.value.addTo(map.value);
        airportLayer.value.eachLayer((layer) => {
          const tooltip = layer.getTooltip();
          if (tooltip) {
            layer.openTooltip();
            tooltip.setOpacity(1);
          }
        });
      }
    }
  };

  /**
   * 销毁地图
   */
  const destroyMap = () => {
    if (map.value && map.value.remove) {
      map.value.remove();
      map.value = null;
    }
  };

  onBeforeUnmount(() => {
    destroyMap();
  });

  return {
    map,
    airportLayerVisible,
    initMap,
    toggleAirportLayer,
    destroyMap
  };
}
